import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, MapPin, Tag, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Event, EventCategory } from '../../services/schedule/types';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: Partial<Event>) => void;
  event?: Partial<Event>;
  selectedDate?: Date;
}

const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  event,
  selectedDate,
}) => {
  const [title, setTitle] = React.useState(event?.title || '');
  const [description, setDescription] = React.useState(event?.description || '');
  const [location, setLocation] = React.useState(event?.location || '');
  const [category, setCategory] = React.useState<EventCategory>(event?.category || 'work');
  const [startTime, setStartTime] = React.useState(
    event?.startTime ? format(new Date(event.startTime), 'HH:mm') : '09:00'
  );
  const [endTime, setEndTime] = React.useState(
    event?.endTime ? format(new Date(event.endTime), 'HH:mm') : '10:00'
  );
  const [remindBefore, setRemindBefore] = React.useState('30');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const date = selectedDate || new Date();
    const startDateTime = new Date(date.setHours(
      parseInt(startTime.split(':')[0]),
      parseInt(startTime.split(':')[1])
    ));
    const endDateTime = new Date(date.setHours(
      parseInt(endTime.split(':')[0]),
      parseInt(endTime.split(':')[1])
    ));

    onSave({
      ...event,
      title,
      description,
      location,
      category,
      startTime: startDateTime,
      endTime: endDateTime,
      reminders: [{
        remindBefore: `PT${remindBefore}M`,
        notificationType: 'popup'
      }]
    });
    onClose();
  };

  const getCategoryColor = (cat: EventCategory) => {
    switch (cat) {
      case 'work':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'personal':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'health':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-lg bg-zinc-900 rounded-3xl shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-light">
                {event?.id ? 'Editar Evento' : 'Novo Evento'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Título do evento"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/90 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-white/20"
                  required
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/50 mb-2">Início</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/90 focus:outline-none focus:ring-2 focus:ring-white/20"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/50 mb-2">Fim</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/90 focus:outline-none focus:ring-2 focus:ring-white/20"
                    required
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm text-white/50 mb-2">Categoria</label>
                <div className="flex gap-2">
                  {(['work', 'personal', 'health'] as EventCategory[]).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={`flex-1 px-4 py-2 rounded-xl border transition-colors ${
                        category === cat
                          ? getCategoryColor(cat)
                          : 'border-white/10 text-white/70 hover:bg-white/5'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="relative">
                <MapPin className="absolute left-4 top-3 w-5 h-5 text-white/30" />
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Localização (opcional)"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white/90 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-white/20"
                />
              </div>

              {/* Description */}
              <div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descrição (opcional)"
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/90 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-white/20 resize-none"
                />
              </div>

              {/* Reminder */}
              <div>
                <label className="block text-sm text-white/50 mb-2">Lembrete</label>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-white/30" />
                  <select
                    value={remindBefore}
                    onChange={(e) => setRemindBefore(e.target.value)}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/90 focus:outline-none focus:ring-2 focus:ring-white/20"
                  >
                    <option value="5">5 minutos antes</option>
                    <option value="10">10 minutos antes</option>
                    <option value="15">15 minutos antes</option>
                    <option value="30">30 minutos antes</option>
                    <option value="60">1 hora antes</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {event?.id ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EventModal;
